import * as React from "react";
import "./streamItem.scss";
import StreamItemIcon, {IconColor} from "./StreamItemIcon";
import DateUtils from "../../../../utils/DateUtils";
import {isNullOrUndefined} from "util";
import AttachmentApi from "../../../../api/AttachmentApi";
import AttachmentImagesPreview from "../../../../components/image/AttachmentImagesPreview";
import IconSmall from "../../atoms/Icon/IconSmall";
import {Icon} from "../../atoms/Icon/Icon";
import ActivityEvent from "../../../../model/event/ActivityEvent";
import Note from "../../../../model/note/Note";
import {MouseEventHandler} from "react";

interface IProps {
	/**
	 * Activity event (LeadEvents and JobEvents)
	 */
	event: ActivityEvent;
	/**
	 * Render complex body message (by default event.getActivityMessage())
	 */
	renderBodyMessage?: () => JSX.Element
	/**
	 * Render extra message if needed
	 */
	renderExtraMessage?: () => JSX.Element
	/**
	 * Array of action which can be perform on event
	 */
	actions?: StreamItemAction[]
	/**
	 * If true, actions are not rendered
	 */
	readOnly?: boolean;
	/**
	 * "X" icon click callback.
	 */
	onDeleteClick?: () => void;
	/**
	 * If true, event creator's display name is not visible.
	 */
	hideCreator?: boolean;
	/**
	 * Custom creator name to be displayed.
	 */
	customCreatorName?: string;
	/**
	 * Stream item icon to be displayed.
	 */
	icon?: string;
	/**
	 * Color of displayed stream item icon background.
	 */
	iconColor?: IconColor;
	/**
	 * Color of stream item icon.
	 */
	iconVariant?: "white" | "orange" | "green";
	/**
	 * Indicates if item is shown not in its parent's context
	 */
	differentContext?: boolean;
}

interface IState {
	/**
	 * Indicates if image is on preview mode
	 */
	isImagePreview: boolean
}

export default class CommonStreamItem extends React.Component<IProps, IState> {

	constructor(props: IProps) {
		super(props);
		this.state = {isImagePreview: false};
		this.onClick = this.onClick.bind(this);
		this.renderEditedIndicator = this.renderEditedIndicator.bind(this);
	}

	render() {
		const event = this.props.event;
		const attachments = event.getAttachments();
		const activeAttachments = attachments.filter((a) => !a.deleted.getOrElse(false));
		return (
			<React.Fragment>
				<div id={event.getId()} className={"m-stream-item " + event.getCssClass(this.props.differentContext)}
					 onClick={activeAttachments.length ? this.onClick : () => {}}>
					<StreamItemIcon icon={this.props.icon ? this.props.icon : event.getIcon()}
									color={this.props.iconColor ? this.props.iconColor : event.getIconColor()}
									variant={this.props.iconVariant}/>
					<div className={"m-stream-item_body"}>
						<div className={"m-stream-item_body_type"}>
							{event.getActivityType(this.props.differentContext)}
							{this.renderEditedIndicator()}
						</div>
						<div className={"m-stream-item_body_message"}>
							{!isNullOrUndefined(this.props.renderBodyMessage) ? this.props.renderBodyMessage() :
								event.getActivityMessage()}
						</div>
						{event.getActivityAdditionalMessages().map((message, index) =>
							<span key={index} className="m-stream-item_body_text">{message}</span>
						)}
						{attachments.map((a, index) => {
							return (
								<span key={index} style={{overflowWrap: "anywhere"}}>
									{a.deleted.getOrElse(false) ?
										<span className={"m-stream-item_body_deleted "}>{`${a.name} (REMOVED)`}</span> :
										<a
											href={AttachmentApi.getAttachmentDownloadUrl(a.id)}
											target="_blank" className={"t-link"} data-name={"download"}>{a.name}
										</a>}
									{a.description.map(() =>
										<React.Fragment>&nbsp;<IconSmall icon={Icon.Note}/></React.Fragment>
									).orNull}
								</span>
							)
						})}
						{!isNullOrUndefined(this.props.renderExtraMessage) ? this.props.renderExtraMessage() : null}
						<div className={"m-stream-item_body_by"}>{this.getCreationInfo(event)}</div>
						{this.renderActions()}
					</div>
					{this.props.onDeleteClick && <IconSmall icon={Icon.X}
															variant={"scarlet"}
															onClick={this.onDeleteClick}/>}
				</div>
				{<AttachmentImagesPreview attachments={activeAttachments}
										  onClose={() => this.setState({isImagePreview: false})}
										  isOpen={this.state.isImagePreview}/>}
			</React.Fragment>
		);
	}

	getCreationInfo = (event: ActivityEvent) => {
		const creationDate = event.getCreationDate().format(DateUtils.DATE_TIME_FORMAT);
		if (this.props.hideCreator) {
			return creationDate;
		}
		const creatorName = this.props.customCreatorName ? this.props.customCreatorName : `By ${event.getUserDisplayName()}`;
		return `${creatorName} on ${creationDate}`;
	};

	renderActions() {
		if (this.props.actions && !this.props.readOnly) {
			const lastActionIndex = this.props.actions.length - 1;
			return (
				<div className={"m-stream-item_actions"}>
					{this.props.actions.map((actionItem, index) =>
						<React.Fragment key={index}>
							<span key={`${actionItem.title}${index}`} onClick={actionItem.action}>{actionItem.title}</span>
							{index !== lastActionIndex &&
							<span key={`${actionItem.title}${index}-s`} className={"m-stream-item_actions_separator"}>|</span>}
						</React.Fragment>
					)}
				</div>
			)
		}
		return null;
	}

	renderEditedIndicator() {
		if (this.props.event instanceof Note) {
			return this.props.event.updateDate.map(
				() => <span className={"m-stream-item_body_type_edited"}> &bull; Edited&nbsp;</span>).orNull
		}
	}

	onClick(event: any) {
		if (event.target.getAttribute("data-name") !== "download") {
			this.setState({isImagePreview: true})
		}
	}

	onDeleteClick = (event: any) => {
		event.stopPropagation();
		this.props.onDeleteClick();
	};
}

export class StreamItemAction {
	constructor(public readonly title: string,
		public readonly action: (evt: React.MouseEvent<HTMLSpanElement>) => any) {}

	static new(title: string, action: () => any) {
		return new StreamItemAction(title, action);
	}
}
